// 100 clientes mock exclusivamente del estado Táchira (Venezuela)
// Generación determinística para estabilidad entre recargas.

const rng = (seed) => {
  let s = seed >>> 0;
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
};

// Municipios/Zonas con centroides aproximados
const MUNICIPIOS = [
  { alias: 'SCY', nombre: 'San Cristóbal', lat: 7.7669, lon: -72.2250 },
  { alias: 'TRB', nombre: 'Táriba', lat: 7.8197, lon: -72.2234 },
  { alias: 'RBO', nombre: 'Rubio', lat: 7.7011, lon: -72.3522 },
  { alias: 'SNA', nombre: 'San Antonio del Táchira', lat: 7.8149, lon: -72.4439 },
  { alias: 'URE', nombre: 'Ureña', lat: 7.9217, lon: -72.4475 },
  { alias: 'LFR', nombre: 'La Fría', lat: 8.2157, lon: -72.2461 },
  { alias: 'CLN', nombre: 'San Juan de Colón', lat: 8.0313, lon: -72.2595 },
  { alias: 'LGT', nombre: 'La Grita', lat: 8.1346, lon: -71.9835 },
  { alias: 'MCH', nombre: 'Michelena', lat: 7.9566, lon: -72.2431 },
  { alias: 'LOB', nombre: 'Lobatera', lat: 7.9133, lon: -72.2474 },
  { alias: 'CDR', nombre: 'Cordero', lat: 7.9113, lon: -72.1629 },
  { alias: 'PLM', nombre: 'Palmira', lat: 7.8370, lon: -72.1681 },
  { alias: 'SJC', nombre: 'San Josecito', lat: 7.7488, lon: -72.2255 },
  { alias: 'STA', nombre: 'Santa Ana del Táchira', lat: 7.8020, lon: -72.2786 },
  { alias: 'CLC', nombre: 'Coloncito', lat: 8.0338, lon: -72.2604 },
  { alias: 'SRP', nombre: 'San Rafael del Piñal', lat: 7.5640, lon: -71.9957 },
  { alias: 'ABJ', nombre: 'Abejales', lat: 7.4839, lon: -71.9408 },
  { alias: 'LTD', nombre: 'La Tendida', lat: 8.2646, lon: -72.2438 },
  { alias: 'QNQ', nombre: 'Queniquea', lat: 8.3005, lon: -72.1036 },
  { alias: 'PRG', nombre: 'Pregonero', lat: 7.4721, lon: -71.7655 },
];

const aliasToMun = Object.fromEntries(MUNICIPIOS.map(m => [m.alias, m]));

// Mapeo propuesto vendedor -> municipios
const VENDOR_MUNS = {
  VEN001: ['SCY', 'SJC'],
  VEN002: ['TRB', 'PLM'],
  VEN003: ['RBO', 'CDR'],
  VEN004: ['SNA', 'URE'],
  VEN005: ['LFR', 'CLC'],
  VEN006: ['LGT', 'LOB'],
  VEN007: ['MCH', 'STA'],
  VEN008: ['SRP', 'ABJ', 'LTD', 'QNQ', 'PRG'],
};

// Objetivo de cantidad por vendedor (13 para los 4 primeros, 12 para los demás) = 100
const VENDOR_TARGETS = {
  VEN001: 13,
  VEN002: 13,
  VEN003: 13,
  VEN004: 13,
  VEN005: 12,
  VEN006: 12,
  VEN007: 12,
  VEN008: 12,
};

const DAYS = ['LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES'];

const pickFrecuencia = (i) => {
  // 60% SEMANAL, 25% QUINCENAL, 15% MENSUAL en bloques determinísticos por i
  const r = i % 20; // 0..19
  if (r < 12) return { freq: 'SEMANAL', semana: '' };
  if (r < 17) return { freq: 'QUINCENAL', semana: (i % 2 === 0 ? 'SEMANA 1-3' : 'SEMANA 2-4') };
  const sem = (i % 4) + 1;
  return { freq: 'MENSUAL', semana: `SEMANA ${sem}` };
};

const formatNum3 = (n) => String(n).padStart(3, '0');

const build = () => {
  const clients = [];
  const rnd = rng(20250825);
  const aliasCounters = Object.fromEntries(MUNICIPIOS.map(m => [m.alias, 0]));

  const vendorList = Object.keys(VENDOR_TARGETS);
  let globalIndex = 0;

  vendorList.forEach(vendor => {
    const target = VENDOR_TARGETS[vendor];
    const aliases = VENDOR_MUNS[vendor];
    for (let i = 0; i < target; i++) {
      const alias = aliases[i % aliases.length];
      const mun = aliasToMun[alias];
      aliasCounters[alias] += 1;
      const localNum = aliasCounters[alias];

      const day = DAYS[(globalIndex + i) % DAYS.length];
      const { freq, semana } = pickFrecuencia(globalIndex + i);

      // Jitter pequeño y determinístico
      const jLat = (rnd() - 0.5) * 0.02; // ~±0.01°
      const jLon = (rnd() - 0.5) * 0.02;

      let lat = (mun.lat + jLat).toFixed(4);
      let lon = (mun.lon + jLon).toFixed(4);

      // 10% sin coordenadas: los últimos 10 de los 100
      const willHideCoords = (globalIndex >= 90);
      if (willHideCoords) { lat = ''; lon = ''; }

      // Valores económicos en rangos similares al mock actual
      const prm_caj = Math.round(25 + rnd() * 70); // 25..95 aprox
      const prm_val = Math.round(9500 + rnd() * 15000); // 9500..24500 aprox

      clients.push({
        clt_codigo: `VE-TAC-${alias}-${formatNum3(localNum)}`,
        clt_nombre: `Comercio ${mun.nombre} ${formatNum3(localNum)}`,
        clt_lat: String(lat),
        clt_lon: String(lon),
        clt_ven_codigo: vendor,
        clt_diavisita: day,
        clt_frecuencia: freq,
        clt_semanavisita: freq === 'SEMANAL' ? '' : semana,
        clt_prm_caj: prm_caj,
        clt_prm_val_caj: prm_val,
      });

      globalIndex += 1;
    }
  });

  return clients;
};

export const clientsTachira = build();
